import MDEditor from "@uiw/react-md-editor";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { ConflictError } from "../../api/ApiUtilities";
import ContainerCard from "../../common/CardContainer";
import { toast } from "../../common/notify";
import SelectInput, { SelectOption } from "../../common/SelectInput";
import TextInput from "../../common/TextInput";
import { Item, ItemTypes } from "../../data/ItemOfBusiness";
import { markdownDiff } from "../diff/markdownDiff";
import { GetItemMarkdown } from "../meetingcommon/MeetingFunctions";
import { ItemConflictBanner, OtherEditorsNotice, PreviousDraft, useEditingPresence } from "./itemEditing";

export interface ItemEditProps {
  item: Item;
  /**
   * The item as other organisers' saves left it (from ItemsUpdated broadcasts): undefined
   * for a new item, null if it was deleted. Used to detect edits made while this form is open.
   */
  latest?: Item | null;
  isAdmin: boolean;
  onSave: (
    heading: string,
    text: string,
    code: number,
    order: number,
    type: string,
    originalText: string,
    diffMode: string,
    moverUserId: string,
    seconderUserId: string,
    markedUpText: string,
    /** the version this form was loaded from; the save is rejected (ConflictError) if the item has changed since */
    version: number | null
  ) => Promise<string>;
  onCancel: () => void;
  onDelete: () => Promise<string>;
}
function ItemEdit({ item, latest, isAdmin, onSave, onCancel, onDelete }: ItemEditProps) {
  const [errors, setErrors] = useState({} as Errors);
  const [saving, setSaving] = useState(false);
  const [heading, setHeading] = useState(item.heading);
  const [text, setText] = useState(item.text);
  const [originalText, setOriginalText] = useState(item.originalText);
  const [markedUpText, setMarkedUpText] = useState(item.markedUpText ?? "");
  const [diffMode, setDiffMode] = useState(item.diffMode);
  const [code, setCode] = useState(parseFloat(item.code));
  const [order, setOrder] = useState(parseFloat(item.code));
  const [type, setType] = useState(item.type);
  const [moverUserId, setMoverUserId] = useState(item.moverUserId);
  const [seconderUserId, setSeconderUserId] = useState(item.seconderUserId);
  // the version the form's content came from, and any newer version saved by someone else
  const [base, setBase] = useState(item);
  const [conflict, setConflict] = useState(null as { latest: Item | null; reason?: string } | null);
  const [previousDraft, setPreviousDraft] = useState("");

  useEditingPresence(item.subId || null);

  const dirty =
    heading !== base.heading ||
    text !== base.text ||
    originalText !== base.originalText ||
    markedUpText !== (base.markedUpText ?? "") ||
    diffMode !== base.diffMode ||
    code !== parseFloat(base.code) ||
    type !== base.type ||
    moverUserId !== base.moverUserId ||
    seconderUserId !== base.seconderUserId;

  function loadFrom(source: Item) {
    setHeading(source.heading);
    setText(source.text);
    setOriginalText(source.originalText);
    setMarkedUpText(source.markedUpText ?? "");
    setDiffMode(source.diffMode);
    setCode(parseFloat(source.code));
    setOrder(parseFloat(source.code));
    setType(source.type);
    setMoverUserId(source.moverUserId);
    setSeconderUserId(source.seconderUserId);
    setBase(source);
  }

  // someone else saved (or deleted) this item while it is open here
  useEffect(() => {
    if (latest === undefined || !item.subId || saving) return;
    if (latest === null) {
      setConflict({ latest: null });
      return;
    }
    if ((latest.version ?? null) === (base.version ?? null)) return;
    if (!dirty) {
      loadFrom(latest);
      toast.info(`${latest.updatedBy || "Someone"} updated this item; the form now shows their version.`);
    } else {
      setConflict({ latest });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest]);

  function loadLatest() {
    if (!conflict?.latest) return;
    setPreviousDraft(text);
    loadFrom(conflict.latest);
    setConflict(null);
  }
  function getNumber(code: string): number {
    return parseFloat(code);
  }
  function itemTypeList(): SelectOption[] {
    return [
      { value: item.type, text: item.type },
      { value: "Original Motion", text: "Original Motion" },
      { value: "Legislation Amendment", text: "Legislation" },
      { value: "Accept Legislation Vote", text: "Final Legislation Vote" },
    ];
  }
  function diffModeList(): SelectOption[] {
    return [
      { value: "Line", text: "Line" },
      { value: "Word", text: "Word" },
      { value: "Sentence", text: "Sentence" },
      { value: "WordWithSpaces", text: "WordWithSpaces" },
    ];
  }
  function formatText(text: string, setFunction: (text: string) => void) {
    console.log(`Format Text: ${text}`);
    //text.replace(/[\r\n]/g, "  \r\n").replace(/([ ]{2}[\r\n]{2})/g, "\r\n\r\n")
    text = text
      .replace(/[\r]?[\n][\r]?[\n]/g, "||||")
      .replace(/[\r]?[\n]/g, "  \r\n")
      .replace(/[|]{4}/g, "\r\n\r\n");
    setFunction(text);
  }

  console.log("Render Items Edit" + item.id + "-" + item.subId + "-" + item.heading);

  interface Errors {
    heading?: string;
    originalText?: string;
    text?: string;
    onSave?: string;
    code?: string;
    order?: string;
    type?: string;
    moverUserId?: string;
    seconderUserId?: string;
  }

  function cancel(event: SyntheticEvent) {
    setSaving(true);
    event.preventDefault();
    onCancel();
  }

  function deleteItem(event: SyntheticEvent) {
    setSaving(true);
    event.preventDefault();
    onDelete()
      .then((error) => {
        setErrors({ onSave: JSON.stringify(error) });
      })
      .finally(() => {
        setSaving(false);
      });
  }

  function save(event: SyntheticEvent) {
    setSaving(true);
    event.preventDefault();
    setErrors({});
    onSave(heading, text, code, order, type, originalText, diffMode, moverUserId, seconderUserId, markedUpText, base.version ?? null)
      .then((error) => {
        if (error) setErrors({ onSave: error });
      })
      .catch((error: Error) => {
        if (error instanceof ConflictError) {
          setConflict({ latest: error.current as Item | null, reason: error.message });
        } else {
          setErrors({ onSave: error.message });
        }
      })
      .finally(() => {
        setSaving(false);
      });
  }

  return (
    <form onSubmit={save}>
      <OtherEditorsNotice subId={item.subId} />
      {conflict ? <ItemConflictBanner latest={conflict.latest} yourText={text} reason={conflict.reason} onLoadLatest={loadLatest} /> : null}
      {previousDraft ? <PreviousDraft text={previousDraft} onDismiss={() => setPreviousDraft("")} /> : null}
      <div className="row">
        <div className="col-8">
          <TextInput name="Heading" label="Heading" type="text" placeholder="" value={heading} error={errors.heading} onChange={setHeading} />
        </div>
        <div className="col-2">
          <SelectInput name="DiffMode" label="Diff Mode" value={diffMode} error={errors.type} onChange={setDiffMode} options={diffModeList()} />
        </div>
        <div className="col-2">
          <SelectInput name="Type" label="Type" value={type} defaultOption="" error={errors.type} onChange={setType} options={itemTypeList()} />
        </div>
      </div>
      {type === ItemTypes.LegislationAmendment ? (
        <div className="row mt-2 p-2">
          <div>
            <MDEditor className="mb-2" value={originalText} onChange={(value) => setOriginalText(value ? value : "")} preview="edit" height={500}></MDEditor>
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="row mt-2 p-2">
        <div className="text-sm">Note: use 2 spaces at the end of a line to make a line break. Use 2 line breaks (enter) to create a paragraph break.</div>
        <div>
          <MDEditor className="mb-2" value={text} onChange={(value) => setText(value ? value : "")} preview="edit" height={500}></MDEditor>
        </div>
      </div>
      {type === ItemTypes.Amendment || type === ItemTypes.LegislationAmendment || markedUpText ? (
        <div className="row mt-2 p-2">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <span className="fw-bold">Mark-up shown to Synod (optional)</span>
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={() => setMarkedUpText(markdownDiff(type === ItemTypes.LegislationAmendment ? originalText : item.text, text, diffMode))}
              title="Generate a draft mark-up from the changes, then correct it by hand"
            >
              Generate from changes
            </button>
            {markedUpText ? (
              <button type="button" className="btn btn-light btn-sm" onClick={() => setMarkedUpText("")}>
                Clear
              </button>
            ) : null}
            <span className="text-muted small">Without mark-up, viewers see an automatically generated diff.</span>
          </div>
          {markedUpText ? (
            <div>
              <MDEditor className="mb-2" value={markedUpText} onChange={(value) => setMarkedUpText(value ? value : "")} preview="edit" height={300}></MDEditor>
            </div>
          ) : null}
        </div>
      ) : null}
      {errors.onSave && (
        <div className="row mt-2 mx-1">
          <div className="alert alert-danger">{errors.onSave}</div>
        </div>
      )}
      <div className="d-flex my-2">
        <div className="px-2 me-auto row">
          <div className="col">
            <TextInput name="Code" label="Code" type="number" placeholder="" value={code.toString()} error={errors.code} onChange={(value) => setCode(getNumber(value))} />
          </div>
          {isAdmin ? (
            <div className="col">
              <TextInput name="Order" label="Order" type="number" placeholder="" value={order.toString()} error={errors.order} onChange={(value) => setOrder(getNumber(value))} />
            </div>
          ) : (
            <></>
          )}
          <div className="col-4">
            <TextInput name="MoverUserId" label="Mover User Id" type="text" placeholder="" value={moverUserId} error={errors.moverUserId} onChange={setMoverUserId} />
          </div>
          <div className="col-4">
            <TextInput name="SeconderUserId" label="Seconder User Id" type="text" placeholder="" value={seconderUserId} error={errors.seconderUserId} onChange={setSeconderUserId} />
          </div>
        </div>
        <div className="px-2 ms-auto">
          <button type="button" disabled={saving} className="btn btn-danger mx-2" onClick={deleteItem}>
            Delete
          </button>
          <button type="button" disabled={saving} className="btn btn-secondary mx-2" onClick={cancel}>
            Cancel
          </button>
          <button type="submit" disabled={saving || (conflict !== null && conflict.latest === null)} className="btn btn-primary mx-2">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
      <div className="m-2 p-3 border rounded sl-prose">
        <div className="text-muted small mb-1">Preview (what attendees will see):</div>
        <MDEditor.Markdown source={markedUpText ? markedUpText : markdownDiff(originalText ? originalText : text, text, diffMode)} />
      </div>
    </form>
  );
}
// {showItemView() && <MDEditor.Markdown source={itemOfBusinessText} />}
// {showItemDiffView() && <MDEditor.Markdown source={diffMarkdownText} />}

export default ItemEdit;
